import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { FilletFeatureDefinitionModel } from '../../../definition/fillet/FilletFeatureDefinition';
import { FilletFeaturePrototype } from '../../../prototype/fillet/FilletFeaturePrototype';
import { FilletPreviewExecution } from './FilletPreviewExecution';
import {
    FeaturePreviewExecutionHandler,
    type FeaturePreviewExecutionInput,
    type FeaturePreviewExecutionResult,
} from '../FeaturePreviewExecutionHandler';

/**
 * 圆角特征预览执行处理器。
 */
export class FilletPreviewExecutionHandler extends FeaturePreviewExecutionHandler {
    /** 圆角预览执行器。 */
    private readonly execution: FilletPreviewExecution;

    /**
     * @param execution 圆角预览执行器。
     */
    public constructor(execution: FilletPreviewExecution) {
        super();
        this.execution = execution;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeaturePreviewExecutionInput): boolean {
        return input.featureKind === 'fillet' && input.definition instanceof FilletFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当预览输入中的定义不是圆角特征定义时。
     */
    public async execute(input: FeaturePreviewExecutionInput): Promise<FeaturePreviewExecutionResult> {
        if (!(input.definition instanceof FilletFeatureDefinitionModel)) {
            throw new Error('圆角预览处理器收到的 definition 类型不正确');
        }

        const prototype = new FilletFeaturePrototype({
            featureId: input.featureId ?? `preview:${Date.now()}`,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId: null,
        });
        const previewResult = input.session
            ? await this.execution.executeEditPreview(
                input.session as FeatureEditSession<FilletFeatureDefinitionModel, FilletFeaturePrototype>,
                input.definition,
            )
            : await this.execution.executeCreatePreview(input.definition);
        return {
            meshData: previewResult.meshData,
            presentation: {
                blendGuides: previewResult.blendGuides ?? [],
            },
            prototype,
            draftDefinition: input.definition,
        };
    }
}
